// Polyfills first — Safari lacks ReadableStream async iteration (pdfjs v6
// getTextContent depends on it). Must install before pdfjs runs.
import './lib/polyfills'

// Better Stack RUM tag — inject before the app boots so it captures the
// initial page view, web vitals, and early exceptions.
import { initBetterStack } from './lib/betterstack'
initBetterStack()

import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
// Fonts via JS import → Vite emits woff2 as hashed assets (CSS @import under
// @tailwindcss/postcss left the url() unresolved). Before index.css so the
// @font-face declarations exist when the design system parses.
import '@fontsource-variable/inter'
import '@fontsource-variable/newsreader'
import './index.css'
import App from './App'
import { AppErrorBoundary } from './components/ErrorBoundary'
import { detectAndCleanIncompatibleCaches } from '@/lib/pwa-cleanup'
import { captureSourceFromUrl } from '@/lib/source-client'

// First-touch acquisition channel: stamp localStorage BEFORE the first auth
// handshake so X-Source is present when getOrCreateUser creates the user row.
captureSourceFromUrl()

// Clean incompatible caches on version change
const cacheVersion = import.meta.env.VITE_CACHE_VERSION || '1.0.0'
void detectAndCleanIncompatibleCaches(cacheVersion).then((result) => {
  if (result.detected && result.cleaned) {
    console.log(
      `[PWA] Cleaned ${result.cachesCleared.length} caches`,
      result.cachesCleared
    )
  }
})

// PWA: manual SW registration (vite-plugin-pwa injectRegister: false).
// Matches the generated stub: gated on 'serviceWorker' in navigator, runs on
// window load. The .catch silences register() rejections in SW-hostile
// environments (Googlebot renderer, Safari private mode) — these are expected
// and were polluting Better Stack as unhandledrejection errors.
// try/catch guards old Safari private mode where even property access throws
// a SecurityError.
try {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => {
        // SW unavailable (crawler renderer / private mode) — expected, ignore.
      })
    })
  }
} catch {
  // Property access itself threw (old Safari private mode) — ignore.
}

const rootEl = document.getElementById('root')!
// The prerendered dist/index.html (also the Pages SPA-fallback shell for
// /app/* etc.) hides #root via inline script unless the URL is '/', to avoid
// a landing-page flash before React boots. Unhide in the same tick as the
// first render so the real view paints immediately.
rootEl.style.display = ''

const root = createRoot(rootEl, {
  // React 19 error handlers — uncaught render errors reach the Better Stack
  // tag via window handlers; keep a console trail for devtools debugging.
  onUncaughtError: (error, errorInfo) => {
    console.warn('Uncaught error', error, errorInfo.componentStack)
  },
  onCaughtError: (error) => {
    console.warn('Caught error', error)
  },
  onRecoverableError: (error) => {
    console.warn('Recoverable error', error)
  },
})

root.render(
  <StrictMode>
    <AppErrorBoundary>
      <BrowserRouter>
        <Routes>
          <Route path="/*" element={<App />} />
        </Routes>
      </BrowserRouter>
    </AppErrorBoundary>
  </StrictMode>,
)
